import { useLazyQuery } from '@apollo/client';
import { Box, Button, ButtonAppearance, ButtonSize, Heading, Icon, IconSize, ModalBody } from '@bechtel/gds';
import { Block, BlockCommodityType, OperatorType } from '@bechtel/sr-types';
import { Formik } from 'formik';
import { filter, get } from 'lodash';
import { FC, useCallback } from 'react';
import { object } from 'yup';
import { FormField, MultiLingualText, TextFieldComponent, useLanguage, useTableMetadataContext } from '~/components';
import { SEARCH_BLOCK, SEARCH_EQUIPMENT, SEARCH_EQUIPMENT_CODE } from '~/gql';
import { useYupSchema } from '~/hooks';
import { FooterButtonStyled, ModalCloseStyled, ModalFooterStyled, ModalHeaderStyled, ModalStyled } from './styled';

interface CopyBlocksFormikState {
  tag: string;
}

const initialValues: CopyBlocksFormikState = {
  tag: '',
};

interface CopyBlocksModalProps {
  onClose: (data?: Block[]) => void;
  blockCommodityType: BlockCommodityType;
}

export const CopyBlocksModal: FC<CopyBlocksModalProps> = ({ onClose, blockCommodityType }) => {
  const { getMultiLingualTextByKey } = useLanguage();
  const tableMetadata = useTableMetadataContext();
  const metadataColumns = filter(get(tableMetadata, 'metadataColumns'), (item) => item.memberName === 'tag');

  const generateSchema = useYupSchema({ metadataColumns });
  const validationSchema = object().shape({
    ...generateSchema(),
  });

  const [searchBlocks] = useLazyQuery(SEARCH_BLOCK);
  const [searchBlockCommodity] = useLazyQuery(
    blockCommodityType === BlockCommodityType.Equipment ? SEARCH_EQUIPMENT : SEARCH_EQUIPMENT_CODE,
    { notifyOnNetworkStatusChange: true }
  );

  const handleSubmit = useCallback(
    async (values: CopyBlocksFormikState) => {
      const { data: searchCommodityData } = await searchBlockCommodity({
        variables: {
          searchInput: {
            filterInput: {
              and: [
                {
                  field: 'tag',
                  operator: OperatorType.EQUAL,
                  value: values.tag,
                },
              ],
            },
          },
        },
      });

      const commodityData = get(
        searchCommodityData,
        blockCommodityType === BlockCommodityType.Equipment ? 'searchEquipments.items' : 'searchEquipmentCodes.items'
      );

      if (!commodityData) return;

      const { data: searchBlocksData } = await searchBlocks({
        variables: {
          searchInput: {
            filterInput: {
              and: [
                {
                  field: 'parentId',
                  value: commodityData[0]?.id,
                  operator: 'EQUAL',
                },
              ],
            },
          },
          blockCommodityType: blockCommodityType,
        },
      });

      if (searchBlocksData?.searchBlocks?.items) {
        onClose(searchBlocksData.searchBlocks.items);
      }
    },
    [blockCommodityType, onClose, searchBlockCommodity, searchBlocks]
  );

  return (
    <ModalStyled visible>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema} validateOnMount>
        {({ handleSubmit, isValid }) => (
          <>
            <ModalHeaderStyled title="">
              <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'space-evenly' }}>
                <Box pl={{ sm: '300' }}>
                  <Heading size="500" weight="500">
                    {getMultiLingualTextByKey('COPY_FROM')}
                  </Heading>
                </Box>
                <ModalCloseStyled onClick={() => onClose()}>
                  <Button size={ButtonSize.SMALL} style={{ background: 'transparent', boxShadow: 'none' }}>
                    <Icon icon="times" iconStyle="regular" size={IconSize.LARGE} />
                  </Button>
                </ModalCloseStyled>
              </Box>
            </ModalHeaderStyled>
            {/* @ts-ignore adjust in GDS component to take child as prop  */}
            <ModalBody
              css={`
                flex-direction: column;
              `}
            >
              <form style={{ width: '100%' }}>
                <Box flexGrow={{ sm: '1' }} p={{ sm: '500' }} pb={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
                  <FormField
                    memberName="tag"
                    required
                    pickListName={
                      blockCommodityType === BlockCommodityType.Equipment ? 'EQUIPMENT.TAG' : 'EQUIPMENT_CODE.TAG'
                    }
                    tagValidationType="NO_DATA_FOUND"
                  >
                    <TextFieldComponent />
                  </FormField>
                </Box>
              </form>
            </ModalBody>
            <ModalFooterStyled>
              <Box p={{ sm: '500' }} pt={{ sm: '200' }}>
                <FooterButtonStyled appearance={ButtonAppearance.SECONDARY} onClick={() => onClose()}>
                  <Box px={{ sm: '500' }}>
                    <MultiLingualText>Cancel</MultiLingualText>
                  </Box>
                </FooterButtonStyled>
                <FooterButtonStyled onClick={() => handleSubmit()} disabled={!isValid}>
                  <Box px={{ sm: '500' }}>
                    <MultiLingualText>OK</MultiLingualText>
                  </Box>
                </FooterButtonStyled>
              </Box>
            </ModalFooterStyled>
          </>
        )}
      </Formik>
    </ModalStyled>
  );
};
